import { useEffect, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import Spinner from "../Components/Spinner";
import { ToastComponent } from "../Components/ToastComponent";
import { alertService } from "../../Services/alert.service";
import { toDateTime } from "../../Services/string.service";

const ALERT_STATUS_TRANSITIONS = {
  1: [2, 3],
  3: [2],
};

export default function AlertDetailsModal({ show, alert, handleClose, onStatusUpdated }) {
  const { t } = useTranslation();

  const [interactions, setInteractions] = useState([]);
  const [isLoadingInteractions, setIsLoadingInteractions] = useState(false);

  const [newInteraction, setNewInteraction] = useState("");
  const [isSavingInteraction, setIsSavingInteraction] = useState(false);

  const [statusOptions, setStatusOptions] = useState([]);
  const [selectedStatusId, setSelectedStatusId] = useState("");
  const [isSavingStatus, setIsSavingStatus] = useState(false);

  const [toastParams, setToastParams] = useState("");
  const showToast = (title, body, color = "Success") =>
    setToastParams(JSON.stringify({ show: true, title, body, color }));

  const loadInteractions = async () => {
    if (!alert?.protocolId) return;
    setIsLoadingInteractions(true);
    try {
      const data = await alertService.fetchAlertDetails({
        platformName: alert.platformName,
        protocolId: alert.protocolId,
      });
      const sorted = [...data].sort(
        (a, b) => new Date(a.generationDate) - new Date(b.generationDate)
      );
      setInteractions(sorted);
    } finally {
      setIsLoadingInteractions(false);
    }
  };

  useEffect(() => {
    if (!show || !alert) return;

    setNewInteraction("");
    setInteractions([]);
    loadInteractions();

    setStatusOptions([]);
    setSelectedStatusId("");
    alertService.fetchProtocolStatuses().then((options) => {
      setStatusOptions(options);
      const allowedIds = ALERT_STATUS_TRANSITIONS[alert.protocolStatusId] ?? [];
      const firstAllowed = options.find((option) =>
        allowedIds.includes(option.protocolStatusId)
      );
      setSelectedStatusId(firstAllowed ? firstAllowed.protocolStatusId : "");
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, alert]);

  const handleAddInteraction = async () => {
    if (!newInteraction.trim()) return;

    setIsSavingInteraction(true);
    try {
      const success = await alertService.createInteraction({
        platformName: alert.platformName,
        protocolId: alert.protocolId,
        interaction: newInteraction.trim(),
      });

      if (success) {
        setNewInteraction("");
        showToast(t("ALERTS.MODAL_TITLE"), t("ALERTS.MODAL.INTERACTION_ADDED_SUCCESS"));
        await loadInteractions();
      } else {
        showToast(t("ALERTS.MODAL_TITLE"), t("ALERTS.MODAL.INTERACTION_ADD_ERROR"), "Danger");
      }
    } finally {
      setIsSavingInteraction(false);
    }
  };

  const handleSaveStatus = async () => {
    if (selectedStatusId === "" || selectedStatusId === undefined) return;

    setIsSavingStatus(true);
    try {
      const success = await alertService.updateProtocolStatus({
        platformName: alert.platformName,
        protocolId: alert.protocolId,
        protocolStatusId: selectedStatusId,
      });

      if (success) {
        const newStatus = statusOptions.find(
          (option) => option.protocolStatusId === Number(selectedStatusId)
        );
        showToast(t("ALERTS.MODAL_TITLE"), t("ALERTS.MODAL.STATUS_UPDATED_SUCCESS"));
        onStatusUpdated?.(alert.protocolId, newStatus);
      } else {
        showToast(t("ALERTS.MODAL_TITLE"), t("ALERTS.MODAL.STATUS_UPDATE_ERROR"), "Danger");
      }
    } finally {
      setIsSavingStatus(false);
    }
  };

  if (!alert) return null;

  const allowedStatusIds = ALERT_STATUS_TRANSITIONS[alert.protocolStatusId] ?? [];
  const allowedStatusOptions = statusOptions.filter((option) =>
    allowedStatusIds.includes(option.protocolStatusId)
  );

  return (
    <>
      <ToastComponent
        params={toastParams}
        closeToast={() => setToastParams(JSON.stringify({ show: false }))}
      />
      <Modal show={show} onHide={handleClose} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{t("ALERTS.MODAL_TITLE")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="row" style={{ fontSize: 13 }}>
            <div className="col-md-6 mb-2">
              <strong>{t("ALERTS.COLUMNS.PLATFORM")}:</strong> {alert.platformName}
            </div>
            <div className="col-md-6 mb-2">
              <strong>{t("ALERTS.COLUMNS.PROTOCOL_ID")}:</strong> {alert.protocolId}
            </div>
            <div className="col-md-6 mb-2">
              <strong>{t("ALERTS.COLUMNS.CLIENT_ID")}:</strong> {alert.clientId}
            </div>
            <div className="col-md-6 mb-2">
              <strong>{t("ALERTS.COLUMNS.DOCUMENT")}:</strong> {alert.document}
            </div>
            <div className="col-md-6 mb-2">
              <strong>{t("ALERTS.COLUMNS.CLIENT")}:</strong> {alert.clientName}
            </div>
            <div className="col-md-6 mb-2">
              <strong>{t("ALERTS.COLUMNS.PHONE")}:</strong> {alert.phone1}
            </div>
            {alert.phone2 && alert.phone2.trim() !== "" && (
              <div className="col-md-6 mb-2">
                <strong>{t("ALERTS.COLUMNS.PHONE_2")}:</strong> {alert.phone2}
              </div>
            )}
            <div className="col-md-6 mb-2">
              <strong>{t("ALERTS.COLUMNS.EVENT_DATE")}:</strong> {toDateTime(alert.eventDate)}
            </div>
            <div className="col-md-6 mb-2">
              <strong>{t("ALERTS.COLUMNS.SUBTYPE")}:</strong> {alert.protocolSubtype || alert.protocolSubType}
            </div>
            <div className="col-md-6 mb-2">
              <strong>{t("ALERTS.COLUMNS.VEHICLE_ID")}:</strong> {alert.vehicleId}
            </div>
            <div className="col-md-6 mb-2">
              <strong>{t("ALERTS.COLUMNS.PLATE")}:</strong> {alert.licensePlate}
            </div>
            <div className="col-md-6 mb-2">
              <strong>{t("ALERTS.COLUMNS.LOCATION")}:</strong> {alert.location}
            </div>
            <div className="col-md-6 mb-2">
              <strong>{t("ALERTS.COLUMNS.LAST_UPDATE")}:</strong> {toDateTime(alert.lastUpdateDate)}
            </div>
          </div>

          <div className="row align-items-end mt-2 mb-3">
            {allowedStatusOptions.length > 0 ? (
              <>
                <div className="col-md-8">
                  <label style={{ fontSize: 13 }}>{t("ALERTS.MODAL.STATUS_LABEL")}</label>
                  <select
                    className="form-select"
                    value={selectedStatusId}
                    onChange={(e) => setSelectedStatusId(e.target.value)}
                  >
                    {allowedStatusOptions.map((option) => (
                      <option key={option.protocolStatusId} value={option.protocolStatusId}>
                        {option.protocolStatus}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-4">
                  <Button
                    variant="primary"
                    size="sm"
                    disabled={isSavingStatus}
                    onClick={handleSaveStatus}
                  >
                    {t("ALERTS.MODAL.SAVE_STATUS_BUTTON")}
                  </Button>
                </div>
              </>
            ) : (
              <div className="col-12" style={{ fontSize: 13 }}>
                <strong>{t("ALERTS.COLUMNS.STATUS")}:</strong> {alert.protocolStatus}
              </div>
            )}
          </div>

          <hr />

          <h6>{t("ALERTS.MODAL.INTERACTIONS_TITLE")}</h6>
          {isLoadingInteractions ? (
            <Spinner />
          ) : interactions.length === 0 ? (
            <div className="text-secondary" style={{ fontSize: 13 }}>
              {t("ALERTS.MODAL.EMPTY_INTERACTIONS")}
            </div>
          ) : (
            <ul className="list-unstyled" style={{ fontSize: 13 }}>
              {interactions.map((item, index) => (
                <li
                  key={index}
                  className="py-2"
                  style={{ borderBottom: "1px solid var(--border-color, #e9ecef)" }}
                >
                  <div className="text-secondary">
                    {toDateTime(item.generationDate)} — {item.userName} ({item.platformName})
                  </div>
                  <div>{item.interaction}</div>
                </li>
              ))}
            </ul>
          )}

          <div className="mt-3">
            <textarea
              className="form-control"
              rows={3}
              placeholder={t("ALERTS.MODAL.NEW_INTERACTION_PLACEHOLDER")}
              value={newInteraction}
              onChange={(e) => setNewInteraction(e.target.value)}
            />
            <div className="mt-2 text-end">
              <Button
                variant="primary"
                size="sm"
                disabled={isSavingInteraction || !newInteraction.trim()}
                onClick={handleAddInteraction}
              >
                {t("ALERTS.MODAL.ADD_INTERACTION_BUTTON")}
              </Button>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            {t("COMMON.CLOSE")}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
